// ARCELE UNEI POLILINII (formatul 3.17, §36): `geometry.bulges`, un număr pe
// MUCHIE, `tan(θ/4)` cu semn — convenția LWPOLYLINE din DXF, vertex cu vertex.
// Semnul: pozitiv ⇔ mergând pe muchie de la i la i+1 unghiul CREȘTE, adică arcul
// întoarce la stânga în reperul cu y în sus. `northUp` întoarce semnul când
// oglindește documentul (ca pe `bulge`), deci aici lista vine deja în reperul
// desenului și se citește ca atare.
//
// Aceeași aritmetică pe care o face producătorul (`canonical_model.arce`), fără
// dependențe: desenul vrea polilinia tesselată ca liniuțele axei să urmeze
// curba (`SiteRoad.path`) și ca o zonă cu margine rotunjită să se umple
// corect. Toleranța implicită e cea a formatului: nicio coardă la mai mult de
// 0,05 m de arcul ei.

/** Punctele arcului de la `a` la `b` cu `bulge`, DUPĂ `a` și până la `b`
 *  inclusiv, cu fiecare coardă la cel mult `tol` de arc. `bulge` 0 (sau o
 *  muchie de lungime zero) dă doar `b`. */
export function teseleazaArc(a: number[], b: number[], bulge: number, tol = 0.05): number[][] {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const coarda = Math.hypot(dx, dy);
  if (!(Math.abs(bulge) > 1e-12) || !(coarda > 1e-9) || !Number.isFinite(bulge)) return [[b[0], b[1]]];
  // §21: d = coarda / 2, săgeata h = |bulge|·d, raza R = d(1 + bulge²) / (2|bulge|)
  const d = coarda / 2;
  const R = (d * (1 + bulge * bulge)) / (2 * Math.abs(bulge));
  // Normala STÂNGĂ a coardei. La bulge pozitiv (întoarcere la stânga, centrul
  // pe stânga) centrul stă la `d(1 − bulge²) / (2·bulge)` pe normala stângă:
  // pozitiv sub semicerc, dincolo de el (|bulge| > 1) trece de partea cealaltă;
  // la bulge negativ semnul întoarce totul, cum trebuie.
  const nx = -dy / coarda, ny = dx / coarda;
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  const la = (d * (1 - bulge * bulge)) / (2 * bulge);
  const cx = mx + nx * la, cy = my + ny * la;
  // unghiul măturat, cu semn: θ = 4·atan(bulge)
  const theta = 4 * Math.atan(bulge);
  // câte coarde: săgeata unei coarde care subîntinde 2φ e R(1 − cos φ) ≤ tol
  const phi = R > tol ? Math.acos(1 - tol / R) : Math.PI / 2;
  const n = Math.max(1, Math.ceil(Math.abs(theta) / (2 * phi)));
  const u0 = Math.atan2(a[1] - cy, a[0] - cx);
  const out: number[][] = [];
  for (let i = 1; i < n; i++) {
    const u = u0 + (theta * i) / n;
    out.push([cx + R * Math.cos(u), cy + R * Math.sin(u)]);
  }
  out.push([b[0], b[1]]);     // capătul exact, nu cel recalculat din unghi
  return out;
}

/** Polilinia cu arcele ei tesselate. `bulges[i]` curbează muchia i → i+1;
 *  pe un inel (`inchis`) ultima intrare închide inelul, iar rezultatul e tot
 *  un inel fără primul punct repetat. Fără `bulges`, sau cu toate 0, polilinia
 *  rămâne NESCHIMBATĂ (aceleași puncte, copiate). */
export function teseleazaBulges(axis: number[][], bulges?: number[] | null,
                                tol = 0.05, inchis = false): number[][] {
  const pts = (axis ?? []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length < 2) return pts.map((p) => [p[0], p[1]]);
  const n = pts.length;
  const muchii = inchis ? n : n - 1;
  const out: number[][] = [[pts[0][0], pts[0][1]]];
  for (let i = 0; i < muchii; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const arc = teseleazaArc(a, b, bulges?.[i] ?? 0, tol);
    // pe inel ultima muchie se întoarce în primul punct, care e deja în listă
    if (inchis && i === muchii - 1) arc.pop();
    out.push(...arc);
  }
  return out;
}

/** Dacă lista chiar curbează ceva: cel puțin o intrare nenulă. O listă de
 *  zerouri (sau lipsă) lasă polilinia dreaptă și nu merită un `path`. */
export const areArce = (bulges?: unknown): bulges is number[] =>
  Array.isArray(bulges) && bulges.some((b) => typeof b === "number" && Math.abs(b) > 1e-12);
